import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import intranetApi from '../../services/intranetApi';
import api from '../../utils/api';
import HolidayManagement from './HolidayManagement';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CogIcon,
  TagIcon,
  CalendarIcon,
  StarIcon,
  AdjustmentsHorizontalIcon,
  UserGroupIcon,
  ChartBarIcon,
  // Icon options for dropdown
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  BellIcon,
  HomeIcon,
  TrophyIcon,
  BookmarkIcon,
  HandThumbUpIcon,
  MapPinIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  UserIcon,
  BuildingOfficeIcon,
  FolderIcon,
  DocumentIcon,
  VideoCameraIcon,
  PhotoIcon,
  GlobeAltIcon,
  HeartIcon,
  LightBulbIcon,
  FireIcon,
  SparklesIcon,
  ShieldCheckIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';

// Icon options for dropdowns
const iconOptions = [
  { name: 'Tag', value: 'TagIcon', component: TagIcon },
  { name: 'Calendar', value: 'CalendarIcon', component: CalendarIcon },
  { name: 'Star', value: 'StarIcon', component: StarIcon },
  { name: 'Document', value: 'DocumentTextIcon', component: DocumentTextIcon },
  { name: 'Chat', value: 'ChatBubbleLeftRightIcon', component: ChatBubbleLeftRightIcon },
  { name: 'Bell', value: 'BellIcon', component: BellIcon },
  { name: 'Home', value: 'HomeIcon', component: HomeIcon },
  { name: 'Trophy', value: 'TrophyIcon', component: TrophyIcon },
  { name: 'Bookmark', value: 'BookmarkIcon', component: BookmarkIcon },
  { name: 'Like', value: 'HandThumbUpIcon', component: HandThumbUpIcon },
  { name: 'Location', value: 'MapPinIcon', component: MapPinIcon },
  { name: 'Clock', value: 'ClockIcon', component: ClockIcon },
  { name: 'Check Circle', value: 'CheckCircleIcon', component: CheckCircleIcon },
  { name: 'Warning', value: 'ExclamationCircleIcon', component: ExclamationCircleIcon },
  { name: 'Info', value: 'InformationCircleIcon', component: InformationCircleIcon },
  { name: 'User', value: 'UserIcon', component: UserIcon },
  { name: 'Building', value: 'BuildingOfficeIcon', component: BuildingOfficeIcon },
  { name: 'Folder', value: 'FolderIcon', component: FolderIcon },
  { name: 'File', value: 'DocumentIcon', component: DocumentIcon },
  { name: 'Video', value: 'VideoCameraIcon', component: VideoCameraIcon },
  { name: 'Photo', value: 'PhotoIcon', component: PhotoIcon },
  { name: 'Globe', value: 'GlobeAltIcon', component: GlobeAltIcon },
  { name: 'Heart', value: 'HeartIcon', component: HeartIcon },
  { name: 'Light Bulb', value: 'LightBulbIcon', component: LightBulbIcon },
  { name: 'Fire', value: 'FireIcon', component: FireIcon },
  { name: 'Sparkles', value: 'SparklesIcon', component: SparklesIcon },
  { name: 'Shield', value: 'ShieldCheckIcon', component: ShieldCheckIcon },
  { name: 'Rocket', value: 'RocketLaunchIcon', component: RocketLaunchIcon }
];

// Color options for dropdowns
const colorOptions = [
  { name: 'Gray', value: '#6B7280' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Yellow', value: '#F59E0B' },
  { name: 'Green', value: '#10B981' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Teal', value: '#14B8A6' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Emerald', value: '#059669' }
];

const CompanyAdmin = () => {
  const { currentUser } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('categories');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Categories state
  const [categories, setCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  // Event types state
  const [eventTypes, setEventTypes] = useState([]);
  const [showEventTypeModal, setShowEventTypeModal] = useState(false);
  const [editingEventType, setEditingEventType] = useState(null);

  // Point settings state
  const [pointSettings, setPointSettings] = useState([]);
  const [showPointModal, setShowPointModal] = useState(false);
  const [editingPointSetting, setEditingPointSetting] = useState(null);

  // Point overrides state
  const [pointOverrides, setPointOverrides] = useState([]);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideStats, setOverrideStats] = useState(null);
  const [users, setUsers] = useState([]);

  // All hooks must be called before any conditional returns
  useEffect(() => {
    // Only load data if user is admin
    if (currentUser && currentUser.role_type === 'admin') {
      loadData();
    }
  }, [activeTab, currentUser]);

  // Check if user is admin
  if (!currentUser || currentUser.role_type !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
          <CogIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You need administrator privileges to access this page.</p>
        </div>
      </div>
    );
  }

  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      switch (activeTab) {
        case 'categories':
          await loadCategories();
          break;
        case 'eventTypes':
          await loadEventTypes();
          break;
        case 'pointSettings':
          await loadPointSettings();
          break;
        case 'pointOverrides':
          await loadPointOverrides();
          await loadOverrideStats();
          break;
        case 'holidayManagement':
          // Load holiday management data
          break;
      }
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    const response = await intranetApi.config.getPostCategories();
    setCategories(response.data.categories);
  };

  const loadEventTypes = async () => {
    const response = await intranetApi.config.getEventTypes();
    setEventTypes(response.data.event_types);
  };

  const loadPointSettings = async () => {
    const response = await intranetApi.config.getPointSettings();
    setPointSettings(response.data.point_settings || []);
  };

  const loadPointOverrides = async () => {
    const response = await intranetApi.points.getPointOverrides();
    setPointOverrides(response.data.overrides || []);
  };

  const loadOverrideStats = async () => {
    const response = await intranetApi.points.getOverrideStats();
    setOverrideStats(response.data.stats || null);
  };

  const showMessage = (message, type = 'success') => {
    if (type === 'success') {
      setSuccess(message);
      setError('');
    } else {
      setError(message);
      setSuccess('');
    }
    setTimeout(() => {
      setSuccess('');
      setError('');
    }, 3000);
  };

  const tabs = [
    { id: 'categories', label: 'Categories', icon: TagIcon },
    { id: 'eventTypes', label: 'Event Types', icon: CalendarIcon },
    { id: 'pointSettings', label: 'Point Settings', icon: StarIcon },
    { id: 'pointOverrides', label: 'Point Overrides', icon: AdjustmentsHorizontalIcon },
    { id: 'holidayManagement', label: 'Holiday Management', icon: HomeIcon }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <CogIcon className="w-8 h-8 text-indigo-600" />
                <h1 className="text-3xl font-bold text-gray-900">Company Administration</h1>
              </div>
              <p className="text-sm text-gray-500">
                Manage categories, event types, points, and user overrides
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
            {success}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm p-2 mb-8">
          <nav className="flex space-x-1">
            {tabs.map(tab => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`${
                    activeTab === tab.id
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  } flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 flex-1 justify-center`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <IconComponent className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-sm">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-8 h-8 border-3 border-gray-200 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
              <p className="text-gray-500">Loading...</p>
            </div>
          ) : (
            <div className="p-6">
              {activeTab === 'categories' && (
                <CategoriesTab
                  categories={categories}
                  onEdit={(category) => {
                    setEditingCategory(category);
                    setShowCategoryModal(true);
                  }}
                  onDelete={async (id) => {
                    try {
                      await intranetApi.config.deletePostCategory(id);
                      showMessage('Category deleted successfully');
                      loadCategories();
                    } catch (err) {
                      showMessage('Failed to delete category: ' + err.message, 'error');
                    }
                  }}
                  onAdd={() => {
                    setEditingCategory(null);
                    setShowCategoryModal(true);
                  }}
                />
              )}

              {activeTab === 'eventTypes' && (
                <EventTypesTab
                  eventTypes={eventTypes}
                  onEdit={(eventType) => {
                    setEditingEventType(eventType);
                    setShowEventTypeModal(true);
                  }}
                  onDelete={async (id) => {
                    try {
                      await intranetApi.config.deleteEventType(id);
                      showMessage('Event type deleted successfully');
                      loadEventTypes();
                    } catch (err) {
                      showMessage('Failed to delete event type: ' + err.message, 'error');
                    }
                  }}
                  onAdd={() => {
                    setEditingEventType(null);
                    setShowEventTypeModal(true);
                  }}
                />
              )}

              {activeTab === 'pointSettings' && (
                <PointSettingsTab
                  pointSettings={pointSettings}
                  onEdit={setEditingPointSetting}
                  onSave={async (actionType, data) => {
                    try {
                      await intranetApi.config.updatePointSettings(actionType, data);
                      showMessage('Point settings updated successfully');
                      loadPointSettings();
                    } catch (err) {
                      showMessage('Failed to update point settings: ' + err.message, 'error');
                    }
                  }}
                />
              )}

              {activeTab === 'pointOverrides' && (
                <PointOverridesTab
                  overrides={(pointOverrides || [])}
                  stats={overrideStats}
                  onAdd={() => setShowOverrideModal(true)}
                  onCreateOverride={async (data) => {
                    try {
                      await intranetApi.points.createPointOverride(data);
                      showMessage('Point override created successfully');
                      loadPointOverrides();
                      loadOverrideStats();
                    } catch (err) {
                      showMessage('Failed to create point override: ' + err.message, 'error');
                    }
                  }}
                />
              )}

              {activeTab === 'holidayManagement' && (
                <HolidayManagement />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCategoryModal && (
        <CategoryModal
          category={editingCategory}
          onClose={() => {
            setShowCategoryModal(false);
            setEditingCategory(null);
          }}
          onSave={async (data) => {
            try {
              if (editingCategory) {
                await intranetApi.config.updatePostCategory(editingCategory.id, data);
                showMessage('Category updated successfully');
              } else {
                await intranetApi.config.createPostCategory(data);
                showMessage('Category created successfully');
              }
              setShowCategoryModal(false);
              setEditingCategory(null);
              loadCategories();
            } catch (err) {
              showMessage('Failed to save category: ' + err.message, 'error');
            }
          }}
        />
      )}

      {showEventTypeModal && (
        <EventTypeModal
          eventType={editingEventType}
          onClose={() => {
            setShowEventTypeModal(false);
            setEditingEventType(null);
          }}
          onSave={async (data) => {
            try {
              if (editingEventType) {
                await intranetApi.config.updateEventType(editingEventType.id, data);
                showMessage('Event type updated successfully');
              } else {
                await intranetApi.config.createEventType(data);
                showMessage('Event type created successfully');
              }
              setShowEventTypeModal(false);
              setEditingEventType(null);
              loadEventTypes();
            } catch (err) {
              showMessage('Failed to save event type: ' + err.message, 'error');
            }
          }}
        />
      )}

      {showOverrideModal && (
        <PointOverrideModal
          onClose={() => setShowOverrideModal(false)}
          onSave={async (data) => {
            try {
              await intranetApi.points.createPointOverride(data);
              showMessage('Point override created successfully');
              setShowOverrideModal(false);
              loadPointOverrides();
              loadOverrideStats();
            } catch (err) {
              showMessage('Failed to create point override: ' + err.message, 'error');
            }
          }}
        />
      )}
    </div>
  );
};

// Categories Tab Component
const CategoriesTab = ({ categories, onEdit, onDelete, onAdd }) => (
  <div>
    <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
      <h2 className="text-xl font-semibold text-gray-900">Post Categories</h2>
      <button 
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={onAdd}
      >
        <PlusIcon className="w-4 h-4 mr-2" />
        Add Category
      </button>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {categories.map(category => (
        <div key={category.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center gap-3">
              <div 
                className="w-4 h-4 rounded-full flex-shrink-0" 
                style={{ backgroundColor: category.color }}
              ></div>
              <div className="min-w-0">
                <h3 className="text-sm font-medium text-gray-900 truncate">{category.display_label}</h3>
                <p className="text-xs text-gray-500 truncate">{category.category_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button 
                className="p-1 text-gray-400 hover:text-indigo-600 rounded"
                onClick={() => onEdit(category)}
              >
                <PencilIcon className="w-4 h-4" />
              </button>
              <button 
                className="p-1 text-gray-400 hover:text-red-600 rounded"
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete this category?')) {
                    onDelete(category.id);
                  }
                }}
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
          {category.description && (
            <p className="text-xs text-gray-600 mb-3 line-clamp-2">{category.description}</p>
          )}
          <div className="flex justify-between items-center text-xs">
            <span className="text-gray-500">Order: {category.sort_order}</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              category.is_active 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {category.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Event Types Tab Component
const EventTypesTab = ({ eventTypes, onEdit, onDelete, onAdd }) => (
  <div>
    <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
      <h2 className="text-xl font-semibold text-gray-900">Event Types</h2>
      <button 
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={onAdd}
      >
        <PlusIcon className="w-4 h-4 mr-2" />
        Add Event Type
      </button>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {eventTypes.map(eventType => (
        <div key={eventType.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center gap-3">
              <div 
                className="w-4 h-4 rounded-full flex-shrink-0" 
                style={{ backgroundColor: eventType.color }}
              ></div>
              <div className="min-w-0">
                <h3 className="text-sm font-medium text-gray-900 truncate">{eventType.display_label}</h3>
                <p className="text-xs text-gray-500 truncate">{eventType.type_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button 
                className="p-1 text-gray-400 hover:text-indigo-600 rounded"
                onClick={() => onEdit(eventType)}
              >
                <PencilIcon className="w-4 h-4" />
              </button>
              <button 
                className="p-1 text-gray-400 hover:text-red-600 rounded"
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete this event type?')) {
                    onDelete(eventType.id);
                  }
                }}
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
          {eventType.description && (
            <p className="text-xs text-gray-600 mb-3 line-clamp-2">{eventType.description}</p>
          )}
          <div className="flex justify-between items-center text-xs">
            <span className="text-gray-500">Order: {eventType.sort_order}</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              eventType.is_active 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {eventType.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Point Settings Tab Component
const PointSettingsTab = ({ pointSettings, onEdit, onSave }) => (
  <div>
    <div className="mb-6 pb-4 border-b border-gray-200">
      <h2 className="text-xl font-semibold text-gray-900">Point Settings</h2>
      <p className="text-sm text-gray-500 mt-1">Configure point values for different actions</p>
    </div>

    <div className="space-y-4">
      {(pointSettings || []).map(setting => (
        <PointSettingCard
          key={setting.id}
          setting={setting}
          onSave={onSave}
        />
      ))}
    </div>
  </div>
);

// Point Setting Card Component
const PointSettingCard = ({ setting, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [points, setPoints] = useState(setting.points_awarded);
  const [isActive, setIsActive] = useState(setting.is_active);

  const handleSave = async () => {
    await onSave(setting.action_type, {
      points_awarded: parseInt(points),
      is_active: isActive
    });
    setIsEditing(false);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
      <div className="flex justify-between items-center">
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-900">{setting.action_label}</h3>
          <p className="text-xs text-gray-500">{setting.action_type}</p>
        </div>
        
        <div className="flex items-center gap-4">
          {isEditing ? (
            <div className="flex items-center gap-3">
              <input
                type="number"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                min="0"
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                Active
              </label>
              <button 
                className="px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700"
                onClick={handleSave}
              >
                Save
              </button>
              <button 
                className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                onClick={() => {
                  setIsEditing(false);
                  setPoints(setting.points_awarded);
                  setIsActive(setting.is_active);
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-900">{setting.points_awarded} points</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                setting.is_active 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {setting.is_active ? 'Active' : 'Inactive'}
              </span>
              <button 
                className="p-1 text-gray-400 hover:text-indigo-600 rounded"
                onClick={() => setIsEditing(true)}
              >
                <PencilIcon className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Point Overrides Tab Component
const PointOverridesTab = ({ overrides, stats, onAdd, onCreateOverride }) => (
  <div>
    <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
      <h2 className="text-xl font-semibold text-gray-900">Point Overrides</h2>
      <button 
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={onAdd}
      >
        <PlusIcon className="w-4 h-4 mr-2" />
        Create Override
      </button>
    </div>

    {stats && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center">
            <ChartBarIcon className="w-8 h-8 text-indigo-600 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{stats.total_overrides}</h3>
              <p className="text-sm text-gray-500">Total Overrides (30 days)</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center">
            <UserGroupIcon className="w-8 h-8 text-indigo-600 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{stats.users_affected}</h3>
              <p className="text-sm text-gray-500">Users Affected</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center">
            <StarIcon className="w-8 h-8 text-green-600 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">+{stats.total_points_added}</h3>
              <p className="text-sm text-gray-500">Points Added</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center">
            <StarIcon className="w-8 h-8 text-red-600 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">-{stats.total_points_subtracted}</h3>
              <p className="text-sm text-gray-500">Points Subtracted</p>
            </div>
          </div>
        </div>
      </div>
    )}

    <div className="space-y-4">
      {(overrides || []).map(override => (
        <div key={override.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-900">{override.user_name}</h4>
              <p className="text-xs text-gray-500">{override.user_email}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                override.adjustment_type === 'add' ? 'bg-green-100 text-green-800' :
                override.adjustment_type === 'subtract' ? 'bg-red-100 text-red-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {override.adjustment_type === 'add' && '+'}
                {override.adjustment_type === 'subtract' && '-'}
                {override.adjustment_type === 'set' && '='}
                {override.points_amount}
              </span>
              <span className="text-xs text-gray-500">
                {new Date(override.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
          <div className="border-t border-gray-100 pt-3">
            <p className="text-sm text-gray-700 mb-2">{override.reason}</p>
            <div className="flex justify-between items-center text-xs text-gray-500">
              <span>Balance: {override.previous_balance} → {override.new_balance}</span>
              <span>by {override.admin_name}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Category Modal Component
const CategoryModal = ({ category, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    category_name: category?.category_name || '',
    display_label: category?.display_label || '',
    description: category?.description || '',
    icon: category?.icon || 'TagIcon',
    color: category?.color || '#3B82F6',
    sort_order: category?.sort_order || 0,
    is_active: category?.is_active !== false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const selectedIcon = iconOptions.find(icon => icon.value === formData.icon);
  const IconComponent = selectedIcon?.component || TagIcon;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl bg-white rounded-lg shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {category ? 'Edit Category' : 'Add Category'}
          </h2>
          <button 
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category Name (Internal)
            </label>
            <input
              type="text"
              value={formData.category_name}
              onChange={(e) => setFormData({...formData, category_name: e.target.value})}
              required
              disabled={!!category}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Label
            </label>
            <input
              type="text"
              value={formData.display_label}
              onChange={(e) => setFormData({...formData, display_label: e.target.value})}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows="3"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Icon
              </label>
              <select
                value={formData.icon}
                onChange={(e) => setFormData({...formData, icon: e.target.value})}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                {iconOptions.map((icon) => (
                  <option key={icon.value} value={icon.value}>
                    {icon.name}
                  </option>
                ))}
              </select>
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                <IconComponent className="w-4 h-4" />
                <span>Preview</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Color
              </label>
              <select
                value={formData.color}
                onChange={(e) => setFormData({...formData, color: e.target.value})}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                {colorOptions.map((color) => (
                  <option key={color.value} value={color.value}>
                    {color.name}
                  </option>
                ))}
              </select>
              <div 
                className="mt-2 h-6 w-full rounded"
                style={{ backgroundColor: formData.color }}
              ></div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort Order
              </label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value)})}
                min="0"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            <div className="flex items-center">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label className="font-medium text-gray-700">Active</label>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {category ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Event Type Modal Component
const EventTypeModal = ({ eventType, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    type_name: eventType?.type_name || '',
    display_label: eventType?.display_label || '',
    description: eventType?.description || '',
    icon: eventType?.icon || 'CalendarIcon',
    color: eventType?.color || '#3B82F6',
    sort_order: eventType?.sort_order || 0,
    is_active: eventType?.is_active !== false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const selectedIcon = iconOptions.find(icon => icon.value === formData.icon);
  const IconComponent = selectedIcon?.component || CalendarIcon;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl bg-white rounded-lg shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {eventType ? 'Edit Event Type' : 'Add Event Type'}
          </h2>
          <button 
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type Name (Internal)
            </label>
            <input
              type="text"
              value={formData.type_name}
              onChange={(e) => setFormData({...formData, type_name: e.target.value})}
              required
              disabled={!!eventType}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Label
            </label>
            <input
              type="text"
              value={formData.display_label}
              onChange={(e) => setFormData({...formData, display_label: e.target.value})}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows="3"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Icon
              </label>
              <select
                value={formData.icon}
                onChange={(e) => setFormData({...formData, icon: e.target.value})}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                {iconOptions.map((icon) => (
                  <option key={icon.value} value={icon.value}>
                    {icon.name}
                  </option>
                ))}
              </select>
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                <IconComponent className="w-4 h-4" />
                <span>Preview</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Color
              </label>
              <select
                value={formData.color}
                onChange={(e) => setFormData({...formData, color: e.target.value})}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                {colorOptions.map((color) => (
                  <option key={color.value} value={color.value}>
                    {color.name}
                  </option>
                ))}
              </select>
              <div 
                className="mt-2 h-6 w-full rounded"
                style={{ backgroundColor: formData.color }}
              ></div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort Order
              </label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value)})}
                min="0"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            <div className="flex items-center">
              <div className="flex items-center h-5">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label className="font-medium text-gray-700">Active</label>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {eventType ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Point Override Modal Component
const PointOverrideModal = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    user_id: '',
    adjustment_type: 'add',
    points_amount: '',
    reason: ''
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Load users for selection
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await api.get('/auth/users');
        setUsers(response.data.users || []);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      points_amount: parseInt(formData.points_amount)
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl bg-white rounded-lg shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Create Point Override</h2>
          <button 
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select User
            </label>
            {loading ? (
              <div className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-50 p-3 text-sm text-gray-500">
                Loading users...
              </div>
            ) : (
              <select
                value={formData.user_id}
                onChange={(e) => setFormData({...formData, user_id: e.target.value})}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value="">Select a user...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adjustment Type
            </label>
            <select
              value={formData.adjustment_type}
              onChange={(e) => setFormData({...formData, adjustment_type: e.target.value})}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="add">Add Points</option>
              <option value="subtract">Subtract Points</option>
              <option value="set">Set Balance</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Points Amount
            </label>
            <input
              type="number"
              value={formData.points_amount}
              onChange={(e) => setFormData({...formData, points_amount: e.target.value})}
              required
              min="0"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason (Required)
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({...formData, reason: e.target.value})}
              required
              rows="3"
              placeholder="Explain why this adjustment is being made..."
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create Override
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyAdmin; 